import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowRight, ShieldCheck, Sparkles, FileText, Mail } from "lucide-react";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.67 — Public CMS page renderer (Privacy / Terms / About / etc.).
 * Fetches /api/pages/{slug} and renders a premium-styled page with:
 *   - branded eyebrow + title hero
 *   - sticky on-page table of contents (jumps to sections)
 *   - section cards with anchor-friendly ids
 *   - bottom CTA
 *
 * All copy is admin-editable via /admin?tab=pages.
 */

const SLUG_META = {
  privacy: { Icon: ShieldCheck, eyebrow: "Privacy & Trust", color: "from-[#16A34A] to-[#22C55E]" },
  terms: { Icon: FileText, eyebrow: "Legal & Service Terms", color: "from-[#0055FF] to-[#7C3AED]" },
  about: { Icon: Sparkles, eyebrow: "About HouseEntry", color: "from-[#0055FF] to-[#06B6D4]" },
  contact: { Icon: Mail, eyebrow: "Get in Touch", color: "from-[#F59E0B] to-[#EA580C]" },
};

const slugifyAnchor = (k) => (k || "").toString().replace(/[^a-z0-9-]/gi, "-").toLowerCase() || "section";

export default function Page() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(null);

  usePageTitle(page?.title ? `${page.title} | HouseEntry` : "HouseEntry Solutions");

  useEffect(() => {
    setPage(null); setErr(null);
    api.get(`/pages/${slug}`)
      .then((r) => setPage(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  const meta = SLUG_META[slug] || { Icon: FileText, eyebrow: "HouseEntry Solutions", color: "from-[#0055FF] to-[#7C3AED]" };

  const tocSections = useMemo(() => {
    if (!page?.sections) return [];
    return page.sections.filter((s) => s.heading).map((s) => ({
      anchor: slugifyAnchor(s.key),
      label: s.heading,
    }));
  }, [page]);

  if (err === "notfound") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="page-notfound">
        <div className="text-center max-w-md">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#A1A1AA] mb-2">404</div>
          <h1 className="font-heading font-bold text-3xl tracking-tight mb-3">Page not found</h1>
          <p className="text-[13px] text-[#52525B] mb-5">
            The page you're looking for doesn't exist. If you believe this is a mistake, let us know.
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[#0055FF] font-semibold text-[13px] hover:underline">
            Back to Home <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return <div className="min-h-[70vh] flex items-center justify-center text-[#A1A1AA] text-[13px]">Loading…</div>;
  }

  const Icon = meta.Icon;

  return (
    <div className="bg-zinc-50 min-h-screen pb-16" data-testid={`cms-page-${slug}`}>
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#09090B] via-[#18181B] to-[#27272A] text-white">
        <div className={`absolute inset-0 bg-gradient-to-br ${meta.color} opacity-20`} />
        <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold">
            <Icon size={12} /> {meta.eyebrow}
          </div>
          <h1 className="font-heading font-bold text-[34px] sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-3" data-testid="cms-page-title">
            {page.title}
          </h1>
          {page.meta?.description && (
            <p className="text-[14.5px] sm:text-[16px] text-zinc-300 max-w-2xl leading-relaxed" data-testid="cms-page-description">
              {page.meta.description}
            </p>
          )}
          <div className="mt-6 flex items-center gap-3 text-[11.5px] text-zinc-400">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> Last updated: {(page.updated_at || "").slice(0, 10) || "—"}</span>
            <span className="text-zinc-600">·</span>
            <Link to="/" className="hover:text-white transition-colors">← Back to Home</Link>
          </div>
        </div>
      </div>

      {/* Body — TOC + sections */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 -mt-8 grid lg:grid-cols-[260px_1fr] gap-6">
        {tocSections.length > 0 && (
          <aside className="hidden lg:block">
            <div className="sticky top-20 bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#52525B] mb-3">On this page</div>
              <nav className="space-y-1.5">
                {tocSections.map((t, i) => (
                  <a
                    key={t.anchor}
                    href={`#${t.anchor}`}
                    className="block text-[12.5px] text-[#3F3F46] hover:text-[#0055FF] hover:bg-[#EEF4FF] px-2 py-1.5 rounded-sm transition-colors"
                    data-testid={`cms-toc-${t.anchor}`}
                  >
                    <span className="text-[#A1A1AA] font-mono mr-2">{(i + 1).toString().padStart(2, "0")}</span>
                    {t.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>
        )}

        <main className={tocSections.length === 0 ? "lg:col-span-2" : ""}>
          <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm divide-y divide-zinc-100">
            {(page.sections || []).map((s, i) => (
              <section
                key={s.key || i}
                id={slugifyAnchor(s.key)}
                className="p-6 sm:p-8 scroll-mt-20"
                data-testid={`cms-section-${s.key || i}`}
              >
                {s.heading && (
                  <h2 className="font-heading font-semibold text-xl sm:text-2xl tracking-tight text-zinc-900 mb-3 flex items-baseline gap-3">
                    <span className="text-[11px] font-mono text-[#0055FF]">{(i + 1).toString().padStart(2, "0")}</span>
                    {s.heading}
                  </h2>
                )}
                {s.image_url && (
                  <img src={s.image_url} alt={s.heading || ""} className="w-full max-w-md rounded-sm mb-4 border border-[#E4E4E7]" loading="lazy" decoding="async" />
                )}
                {s.body && (
                  <div className="text-[14px] text-[#3F3F46] whitespace-pre-line leading-[1.7]">
                    {s.body}
                  </div>
                )}
                {s.cta_text && s.cta_link && (
                  <Link
                    to={s.cta_link}
                    className="inline-flex items-center gap-1.5 mt-5 btn-gradient text-white font-bold text-[13px] px-5 py-2.5 rounded-xl shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95 transition-all"
                    data-testid={`cms-cta-${s.key}`}
                  >
                    {s.cta_text} <ArrowRight size={13} />
                  </Link>
                )}
              </section>
            ))}
          </div>

          <div className="mt-6 text-center text-[11px] text-[#71717A]">
            Need help? Email <a href="mailto:houseentrysolutions@gmail.com" className="text-[#0055FF] hover:underline">houseentrysolutions@gmail.com</a> · We respond within 24 hours.
          </div>
        </main>
      </div>
    </div>
  );
}
